import { useState, useCallback } from 'react';
import { ImageTransform, Annotation } from '@/utils/imageProcessing';

interface PinMarker {
  id: string;
  x: number;
  y: number;
  label: string;
  color: string;
  aiPredictionIndex?: number;
}

export interface HistorySnapshot {
  annotations: Annotation[];
  pins: PinMarker[];
  transform: ImageTransform;
  timestamp: number;
}

const MAX_HISTORY = 50;

export const useEditorHistory = () => {
  const [history, setHistory] = useState<HistorySnapshot[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const saveSnapshot = useCallback((snapshot: Omit<HistorySnapshot, 'timestamp'>) => {
    setHistory((prev) => {
      const newHistory = prev.slice(0, currentIndex + 1);
      newHistory.push({
        ...snapshot,
        timestamp: Date.now(),
      });
      
      // Keep only last MAX_HISTORY snapshots
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
        return newHistory;
      }
      
      return newHistory;
    });
    
    setCurrentIndex((prev) => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [currentIndex]);

  const undo = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      return history[newIndex];
    }
    return null;
  }, [currentIndex, history]);

  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      return history[newIndex];
    }
    return null;
  }, [currentIndex, history]);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  const clearHistory = useCallback(() => {
    setHistory([]);
    setCurrentIndex(-1);
  }, []);

  return {
    saveSnapshot,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  };
};
